import { HtmlMessageComponent } from "../components/html-message.component";
import RatingWidget from "../components/widgets/rating.widget.component";
import { InlineUserInput } from "../components/inline-user-input.component";
import { PrettyButtonGroup } from "../components/pretty-button-group.component";

/**
 * Calculate pricing summary (price and tax) from selected items
 * @param {Array} selectedItems - Array of selected price blocks and services
 * @param {string} term - Either "total" or "application" to determine which pricing to use
 * @param {Object} [props] - Optional props object for crmSolution and serviceTax
 * @returns {Object} Summary object with price and tax properties
 */
export const calculatePricingSummary = (
  selectedItems,
  term,
  companyProps = {},
) => {
  const termKey = term === "total" ? "total" : "application";
  const isRealGreen = companyProps?.crmSolution === "real-green";
  const totalPrice = selectedItems.reduce((sum, item) => {
    return sum + (item.calculatedPricing?.[termKey]?.displayPrice ?? 0);
  }, 0);

  let totalTax;
  if (isRealGreen) {
    totalTax = selectedItems.reduce((sum, item) => {
      return sum + (item.calculatedPricing?.[termKey]?.tax ?? 0);
    }, 0);
  } else {
    // For non-RealGreen, derive tax from totalPrice and serviceTax
    const serviceTax = companyProps.serviceTax || 0;
    totalTax = (totalPrice * serviceTax) / 100;
  }

  return {
    price: Math.round((totalPrice + Number.EPSILON) * 100) / 100,
    tax: Math.round((totalTax + Number.EPSILON) * 100) / 100,
  };
};

export const displayTotalAmount = (selectedItems, term, company, currency) => {
  const summary = calculatePricingSummary(selectedItems, term, company);
  const t = summary.tax;

  // biome-ignore lint/suspicious/noGlobalIsNan: Number.isNaN is not the same as isNaN
  if (isNaN(t)) {
    return (
      <div style={{ width: "100%", display: "inline" }}>
        <span style={{ width: "100%" }}>
          {currency}
          {summary.price.toFixed(2)}
        </span>
        <span style={{ color: "red", fontSize: 14, fontWeight: "bold" }}>
          {company.taxOverride ? ` ${t}` : ""}
        </span>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", display: "inline" }}>
      {currency}
      {(summary.price + t).toFixed(2)}
    </div>
  );
};

export const displayMonthPaymentAmount = (
  selectedItems,
  term,
  company,
  currency,
) => {
  const monthlyItems = selectedItems.filter((i) => {
    return i.services.application[0].usesUpfrontPricing;
  });

  let monthlyPrice = 0;
  // biome-ignore lint/complexity/noForEach: <explanation>
  monthlyItems.forEach((item) => {
    monthlyPrice += item.services.application[0].recurringPrice;
  });

  return (
    <div style={{ width: "100%", display: "inline" }}>
      <span style={{ width: "100%" }}>
        {currency}
        {monthlyPrice.toFixed(2)}
      </span>
    </div>
  );
};

export function populateQuestion(questions) {
  let qobject = {};
  if (!questions.length) {
    qobject = {
      current_question: null,
      current_type: null,
      current_options: null,
      current_messages: null,
      next: false,
    };
  } else {
    const q = questions.shift();
    qobject = {
      current_question: q.question,
      current_type: q.type,
      current_options: q.options,
      current_messages: q.followup,
    };
    if (!questions.length) {
      qobject.next = false;
    } else {
      qobject.next = true;
    }
  }
  return qobject;
}

export function clearModalPriceAlerts() {
  //$("#price-breakdown").html('');
}

export function createModalProgramPricingAlert(programInfo) {
  const html = `<div class="alert alert-secondary" role="alert">
    <div class="action-buttons"><button class="circle plus" style="display:none;"></button>
                <button class="circle minus" ></button>
            </div>
        <h4 class="program-name">Program Name</h4>
        <div class="program-description">Description of program
                <span class="program-cost">$500</span>
        </div>
</div>`;
  return html;
  // $("#price-breakdown").append(html);
}

export function generateStepTrigger(program, self) {
  const questions = program.probing_questions || [];
  const steps = [];
  if (!questions) return [];
  // biome-ignore lint/complexity/noForEach: <explanation>
  questions.forEach((q) => {
    const conditionals = q.conditionals || [];

    if (q.component) {
      switch (q.component.type) {
        case "html": {
          const html = q.component.html;
          const newc = <HtmlMessageComponent />;
          q.component = newc;
          q.inputAttributes = { html: html };
          break;
        }
        case "rating": {
          const placeholder = q.component.placeholder || 3;
          const newcc = <RatingWidget placeholder={placeholder} />;
          q.component = newcc;
          q.trigger = (payload) => {
            const val = payload.value;

            if (val !== "5") {
              return "9orless-lawn";
            }
            return "10-lawn";
          };
          break;
        }
        case "userinput": {
          const ph = q.component.placeholder || "Enter Input";
          const newinput = (
            <InlineUserInput id={"utils-input"} placeholder={ph} />
          );
          q.component = newinput;

          break;
        }
        case "usernumberinput": {
          const ph = q.component.placeholder || "Enter Input";
          const minValue = q.component.min || 0;
          const maxValue = q.component.max || Number.POSITIVE_INFINITY;
          const subtype = q.component.subtype;
          const newinput = (
            <InlineUserInput
              id={"utils-number-input"}
              validator={(value) => {
                const trimmedValue = `${value}`.toLowerCase().trim();
                if (subtype === "wholeNumber" && trimmedValue.includes(".")) {
                  return "Please enter a whole number.";
                }
                if (
                  trimmedValue.length < 1 ||
                  trimmedValue.includes("e") ||
                  // biome-ignore lint/suspicious/noGlobalIsNan: This is required
                  isNaN(value)
                ) {
                  return subtype === "wholeNumber"
                    ? "Please enter a whole number."
                    : "Please enter a valid number.";
                }

                if (Number(value) < minValue) {
                  return `Please enter a number greater than or equal to ${minValue}.`;
                }
                if (Number(value) > maxValue) {
                  return `Please enter a number less than or equal to ${maxValue}.`;
                }

                return true;
              }}
              placeholder={ph}
              type="number"
            />
          );
          q.component = newinput;
          break;
        }
        case "zoneinput": {
          const ph = q.component.placeholder || "Enter Input";
          const newinput = (
            <InlineUserInput
              id={"utils-zone-input"}
              placeholder={ph}
              customButton={true}
              defaultInputMessage={
                q.metadata ? q.metadata.defaultInputMessage : ""
              }
            />
          );
          q.component = newinput;
          break;
        }
        case "pretty-button-group": {
          const newc = (
            <PrettyButtonGroup
              buttons={q.component.buttons}
              self={self}
              step={q}
            />
          );
          q.component = newc;
          break;
        }
        default:
      }
    }

    let trigger = !conditionals.length ? q.trigger : null;
    if (!trigger) {
      trigger = recordLawnbotV1Answer(self, q, null, conditionals);
    }

    if (q.options) {
      q.options = q.options.map((p) => {
        if (typeof p === "object") {
          return {
            ...p,
            trigger: recordLawnbotV1Answer(self, q, trigger),
          };
        }
        return {
          value: q,
          label: q,
          trigger: recordLawnbotV1Answer(self, q, trigger),
        };
      });
    } else if (!q.trigger) {
      q.trigger = recordLawnbotV1Answer(self, q, trigger);
    } else if (typeof q.trigger === "string" && q.setVariable) {
      q.trigger = recordLawnbotV1Answer(self, q, q.trigger);
    }

    steps.push(q);
  });
  return steps;
}

export function toTitleCase(str) {
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );
}
const recordLawnbotV1Answer = (self, step, trigger, conditionals) => {
  function setVariableCheck(value) {
    if (typeof step.setVariable !== "undefined") {
      const recordValue =
        step.metadata &&
        value === step.metadata.defaultInputMessage &&
        step.metadata.defaultValue
          ? step.metadata.defaultValue
          : value;
      self.answers[step.setVariable] = recordValue;

      const payload = {};
      payload[step.setVariable] = recordValue;
      self.recordChatRecord({ answers: payload });
    }
  }

  return ({ value }) => {
    const next = conditionals
      ? conditionals.filter(
          (i) =>
            i.matches === value ||
            (i.regex && value.match(new RegExp(i.regex))),
        )
      : [{ trigger }];
    if (next.length) {
      setVariableCheck(value);
      return next[0].trigger ? next[0].trigger : "handle-error";
    }
    return "handle-error";
  };
};

/**
 * Determines if probing questions are enabled for a given program name.
 * Handles cases where multiple entries exist for the same programName with different useProbingQuestions values.
 *
 * Business Rules:
 * 1. If at least one record for programName has useProbingQuestions: true → returns true
 * 2. If all records for programName have useProbingQuestions: false → returns false
 * 3. If no records exist for programName → returns false
 *
 * Note: The programDetails array received from the API is already pre-filtered
 * by the backend to only include active programs (those in availableServiceTypes).
 * This filtering happens in mapCompanyAndBotFromIds() in the Dashboard API.
 *
 * This ensures that:
 * - Only active programs are evaluated for probing questions
 * - If any configuration for an active program has probing questions enabled,
 *   the "Customize My Quote" option will be shown
 *
 * @param {string} programName - The program name to check (e.g., "Mowing", "Fertilizer & Weed Control")
 * @param {Array<Object>} programDetails - Array of program detail objects from the bot configuration (pre-filtered to active programs)
 * @param {string} programDetails[].programName - The name of the program
 * @param {boolean} programDetails[].useProbingQuestions - Whether probing questions are enabled for this entry
 * @param {boolean} programDetails[].active - Whether the program is currently active
 * @param {Object|Array} programDetails[].probingQuestions - The probing questions configuration
 *
 * @returns {boolean} True if probing questions should be enabled for this program, false otherwise
 *
 * @example
 * // Multiple records for same program, at least one has probing enabled - returns true
 * const programDetails = [
 *   { programName: "Pest Control", useProbingQuestions: false, probingQuestions: [] },
 *   { programName: "Pest Control", useProbingQuestions: true, probingQuestions: {...} }
 * ];
 * hasProbingQuestionsEnabled("Pest Control", programDetails); // Returns true → Show Quote Options
 *
 * @example
 * // All records have probing disabled - returns false
 * const programDetails = [
 *   { programName: "Fertilizer & Weed Control", useProbingQuestions: false, probingQuestions: [] },
 *   { programName: "Fertilizer & Weed Control", useProbingQuestions: false, probingQuestions: [] }
 * ];
 * hasProbingQuestionsEnabled("Fertilizer & Weed Control", programDetails); // Returns false → Direct to Checkout
 *
 * @example
 * // Mixed: one with probing true, one with probing false - returns true (at least one has true)
 * const programDetails = [
 *   { programName: "Mowing", useProbingQuestions: true, active: true, probingQuestions: {...} }
 * ];
 * hasProbingQuestionsEnabled("Mowing", programDetails); // Returns true
 */
export const hasProbingQuestionsEnabled = (programName, programDetails) => {
  // Early return for invalid inputs
  if (!programName || typeof programName !== "string") {
    return false;
  }

  if (!programDetails || !Array.isArray(programDetails)) {
    return false;
  }

  // Filter all entries matching the program name
  const programEntries = programDetails.filter(
    (config) =>
      config &&
      typeof config === "object" &&
      config.programName === programName,
  );

  // If no entries found for this program, probing questions are not enabled
  if (programEntries.length === 0) {
    return false;
  }

  // Check if at least one entry has useProbingQuestions set to true
  // Note: The "active" check is no longer needed because:
  // 1. The programDetails array is pre-filtered by the backend API
  // 2. Only programs in availableServiceTypes (active programs) are included
  // 3. This function is only called for selectedPrograms which are already active
  return programEntries.some((entry) => entry.useProbingQuestions === true);
};
